import type { ReactNode } from 'react';
import { cn } from '../utils/cn';
import { formatFullNumber } from '../utils/formatters';
import { RESOURCE_LABELS, resourceEntries, type ResourceQuantityMap } from '../utils/resourceQuantities';
import { resourceCostGlyph } from './resourceCostIcons';

interface ResourceQuantitiesProps {
    amounts: ResourceQuantityMap;
    empty?: ReactNode;
    includeZero?: boolean;
    className?: string;
}

/**
 * Inline icon-plus-amount run for a cost, refund, cargo or loot line. The one
 * renderer for resource quantities in running text; `ResourceCostList` is the
 * stacked variant used beside thumbnails.
 */
export default function ResourceQuantities({ amounts, empty = '—', includeZero, className }: ResourceQuantitiesProps) {
    const entries = resourceEntries(amounts, { includeZero });

    if (entries.length === 0) {
        return <span className={className}>{empty}</span>;
    }

    return (
        <span className={cn('inline-flex flex-wrap items-center gap-x-3 gap-y-1 align-middle', className)}>
            {entries.map(([key, value]) => (
                <span key={key} data-amount={key} title={RESOURCE_LABELS[key]} className="inline-flex items-center gap-1 whitespace-nowrap">
                    {resourceCostGlyph(key)}
                    <span>{formatFullNumber(value)}</span>
                    <span className="sr-only">{` ${RESOURCE_LABELS[key]}`}</span>
                </span>
            ))}
        </span>
    );
}
